import { useState } from 'react'
import { ChevronRightIcon, Bars3Icon } from '@heroicons/react/24/outline'
import { Moon, Sun } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useCurrentAdvisor } from '../hooks/useCurrentAdvisor'
import { useSystemStatus } from '../hooks/useSystemStatus'
import { NotificationBell } from './Common/NotificationBell'
import { AdvisorAvatar } from './Common/AdvisorAvatar'
import { StatusDashboard } from './Common/StatusDashboard'

const APP_VERSION = '3.2.0'

export function Header({ breadcrumbs, sidebarCollapsed, onToggleCollapse, isDarkMode, onToggleDarkMode, isMobileOpen, onMobileToggle, onNavigate }) {
  const { user } = useAuth()
  const { advisor } = useCurrentAdvisor()
  const { data: systemStatus } = useSystemStatus()
  const [showStatus, setShowStatus] = useState(false)
  const isOwner = advisor?.is_owner === true

  return (
    <div className={`sticky top-0 z-40 flex-shrink-0 transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-900' : 'bg-white'
    }`}>

      {/* Top Row: Logo zone + Brand + User — golden line at bottom */}
      <div className={`flex items-stretch border-b-4 border-sfp-orange transition-colors duration-300 ${
        isDarkMode ? 'bg-slate-900' : 'bg-white'
      }`}>

        {/* Logo zone — responsive width: 80px mobile, 300px desktop */}
        <div className={`flex items-center justify-center border-r px-4 py-4 flex-shrink-0 transition-colors duration-300 w-20 lg:w-[300px] ${
          isDarkMode ? 'border-slate-800' : 'border-gray-200'
        }`}>
          <div className={`hidden lg:flex items-center font-bold text-lg transition-colors duration-300 ${
            isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'
          }`}>
            SFP Wealth
          </div>
          <div className={`w-12 h-12 rounded-md items-center justify-center lg:hidden flex font-bold text-sm transition-colors duration-300 ${
            isDarkMode
              ? 'bg-slate-600 text-slate-100'
              : 'bg-sfp-orange text-white'
          }`}>
            <span>SFP</span>
          </div>
        </div>

        {/* Brand + User */}
        <div className="flex-1 flex items-center justify-between px-6 py-3">
          <span className={`text-lg font-medium tracking-widest transition-colors duration-300 ${
            isDarkMode ? 'text-slate-400' : 'text-gray-500'
          }`}>
            SFP Wealth
          </span>
          {user && (
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className={`text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-slate-400' : 'text-gray-500'
                }`}>Signed in as</p>
                <p className={`text-sm font-semibold leading-tight transition-colors duration-300 ${
                  isDarkMode ? 'text-slate-200' : 'text-sfp-darkgray'
                }`}>{advisor?.full_name ?? user.email}</p>
              </div>
              <div className="flex items-center gap-1">
                {/* Status dot — only for owner account */}
                {isOwner && (
                  <button
                    onClick={() => setShowStatus(true)}
                    title={systemStatus?.status === 'ok' ? 'System OK — click for details' : 'System issue — click for details'}
                    className={`p-1 rounded transition ${
                      isDarkMode
                        ? 'hover:bg-slate-700 text-slate-400'
                        : 'hover:bg-gray-100 text-gray-500'
                    }`}
                  >
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                      !systemStatus ? 'bg-gray-300' :
                      systemStatus.status === 'ok' ? 'bg-green-500' : 'bg-red-500'
                    }`} />
                  </button>
                )}
                {/* Version badge */}
                <div className={`text-xs px-2 py-1 rounded transition-colors duration-300 ${
                  isDarkMode
                    ? 'bg-slate-800 text-slate-400'
                    : 'bg-gray-100 text-gray-600'
                }`} title={`Version ${APP_VERSION}`}>
                  v{APP_VERSION}
                </div>
              </div>
              <AdvisorAvatar
                avatarUrl={advisor?.avatar_url ?? null}
                fullName={advisor?.full_name ?? user?.email ?? ''}
                size="sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Bottom Row: Toggle + Breadcrumbs — below the golden line */}
      <div className={`flex items-stretch border-b transition-colors duration-300 ${
        isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-gray-100 bg-white'
      }`}>

        {/* Desktop collapse toggle */}
        <div className={`hidden lg:flex items-center justify-center border-r px-2 py-1 transition-all duration-300 flex-shrink-0 ${
          isDarkMode ? 'border-slate-800' : 'border-gray-200'
        }`} style={{ width: sidebarCollapsed ? 64 : 280 }}>
          <button
            onClick={onToggleCollapse}
            className={`w-full p-2 rounded transition flex items-center justify-center ${
              isDarkMode
                ? 'hover:bg-slate-800 text-slate-400'
                : 'hover:bg-gray-100 text-sfp-darkgray'
            }`}
            title={sidebarCollapsed ? 'Expand' : 'Collapse'}
          >
            <ChevronRightIcon className={`w-4 h-4 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Mobile hamburger button */}
        <div className={`flex lg:hidden items-center justify-center border-r px-2 py-1 flex-shrink-0 ${
          isDarkMode ? 'border-slate-800' : 'border-gray-200'
        }`}>
          <button
            onClick={() => onMobileToggle(!isMobileOpen)}
            className={`p-2 rounded transition ${
              isDarkMode
                ? 'hover:bg-slate-800 text-slate-400'
                : 'hover:bg-gray-100 text-sfp-darkgray'
            }`}
            title="Toggle menu"
          >
            <Bars3Icon className="w-5 h-5" />
          </button>
        </div>

        {/* Breadcrumbs + Dark Mode Toggle */}
        <div className="flex-1 flex items-center justify-between gap-1 px-6 py-2">
          <div className="flex items-center gap-1">
            {breadcrumbs?.map((item, idx) => (
              <span key={idx} className="flex items-center gap-1">
                {idx > 0 && (
                  <ChevronRightIcon className={`w-3 h-3 flex-shrink-0 transition-colors duration-300 ${
                    isDarkMode ? 'text-slate-600' : 'text-gray-400'
                  }`} />
                )}
                <span className={
                  item.current
                    ? 'text-sfp-orange font-semibold text-sm'
                    : `text-sm transition-colors duration-300 ${
                        isDarkMode
                          ? 'text-slate-500 hover:text-slate-300'
                          : 'text-gray-500 hover:text-sfp-darkgray'
                      }`
                }>
                  {item.label}
                </span>
              </span>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <NotificationBell isDarkMode={isDarkMode} onNavigate={onNavigate} />

            {/* Dark Mode Toggle Button */}
            {onToggleDarkMode && (
              <button
                onClick={onToggleDarkMode}
                className={`p-2 rounded transition-all duration-300 flex-shrink-0 ${
                  isDarkMode
                    ? 'bg-slate-700 hover:bg-slate-600 text-amber-400'
                    : 'bg-gray-100 hover:bg-gray-200 text-sfp-orange'
                }`}
                aria-label="Toggle dark mode"
                title={isDarkMode ? 'Light mode' : 'Dark mode'}
              >
                {isDarkMode ? (
                  <Sun className="w-4 h-4" />
                ) : (
                  <Moon className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>

      </div>

      {/* Status Dashboard modal — owner only */}
      {isOwner && showStatus && (
        <StatusDashboard onClose={() => setShowStatus(false)} isDarkMode={isDarkMode} />
      )}

    </div>
  )
}
